import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, FormControlLabel, Grid, InputAdornment, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { ArrowForward, Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getAddressFieldReference,
  getMasters,
  type AddressGeoLevelDef,
} from '../../services/platform/masterService';
import {
  ADDRESS_FIELD_LABELS,
  ADDRESS_FIELD_MAX_SORT_ORDER,
  addressFieldKeyFromName,
  geoOf,
  nextAddressFieldSortOrder,
  normalizeAddressFieldName,
  orderOf,
  validateAddressFieldForm,
  type AddressFieldExistingRecord,
} from '../../utils/addressFieldMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const lockAdornment = {
  readOnly: true,
  endAdornment: (
    <InputAdornment position="end">
      <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
    </InputAdornment>
  ),
};
const HIERARCHY = ['country', 'state', 'city', 'district'];

export default function AddressFieldFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [keyEdited, setKeyEdited] = useState(false);
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const meta = form.metadata;

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['address-field-reference'],
    queryFn: getAddressFieldReference,
    enabled: open,
    staleTime: 60 * 60_000,
  });
  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'address_fields', 'form-siblings'],
    queryFn: () => getMasters('address_fields', { limit: 500, page: 1 }),
    enabled: open,
    staleTime: 0,
  });
  const existing = useMemo<AddressFieldExistingRecord[]>(
    () => ((existingRaw?.data ?? []) as AddressFieldExistingRecord[]),
    [existingRaw],
  );
  const geoLevels = useMemo(() => reference?.geoLevels ?? [], [reference]);
  const geo = geoLevels.find((g) => g.value === str(meta.geoLevel));
  const others = existing.filter((r) => r._id !== editingId);
  const takenGeo = new Map(others.map((r) => [geoOf(r), r.name]));
  const suggestedSortOrder = useMemo(() => nextAddressFieldSortOrder(existing, editingId), [existing, editingId]);
  const editingRow = existing.find((r) => r._id === editingId);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    setKeyEdited(editing);
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', isActive: true, sortOrder: 0, metadata: { required: false } });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateAddressFieldForm(
      { name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { existing, editingId, geoLevels, isActive: form.isActive !== false },
    ),
    [form.name, form.description, form.sortOrder, form.isActive, meta, existing, editingId, geoLevels],
  );
  const blocking = Object.keys(errors).map((key) => [key, ADDRESS_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading && !referenceLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already|reserved/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const setMeta = (patch: Record<string, string | number | boolean>, extra: Partial<MasterFormState> = {}) => {
    const metadata = { ...meta, ...patch };
    onChange({ ...form, ...extra, metadata, code: str(metadata.fieldKey) });
  };

  const applyGeo = (def: AddressGeoLevelDef | undefined) => {
    if (!def) return;
    const fieldKey = def.fieldKey ?? (keyEdited ? str(meta.fieldKey) : addressFieldKeyFromName(normalizeAddressFieldName(form.name)));
    const fieldType = def.fieldTypes.includes(str(meta.fieldType) as never) ? str(meta.fieldType) : def.fieldTypes[0];
    const source = def.sources.includes(str(meta.source) as never) ? str(meta.source) : def.sources[0];
    setMeta({ geoLevel: def.value, fieldKey, fieldType, source, required: def.anchor ? true : meta.required === true });
    touch('geoLevel');
  };

  const handleNameChange = (raw: string) => {
    const name = limitConsecutiveRepeatedCharacters(raw).replace(/^\s+/, '');
    const patch: Record<string, string | number | boolean> = {};
    if (!editing && geo && !geo.fieldKey && !keyEdited) patch.fieldKey = addressFieldKeyFromName(normalizeAddressFieldName(name));
    setMeta(patch, { name });
    touch('name');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const actionLabel = editing ? 'Update' : 'Create';
  const typeDefs = reference?.fieldTypes ?? [];
  const sourceDefs = reference?.sources ?? [];
  const typeHint = typeDefs.find((t) => t.value === str(meta.fieldType))?.hint;
  const sourceHint = sourceDefs.find((s) => s.value === str(meta.source))?.hint;
  const parentDef = geo?.parent ? geoLevels.find((g) => g.value === geo.parent) : undefined;
  const parentRow = parentDef ? others.find((r) => geoOf(r) === parentDef.value) : undefined;
  const lockNote = editingRow && (editingRow as { toggleLock?: string | null }).toggleLock;
  const deleteNote = editingRow && (editingRow as { deleteLock?: string | null }).deleteLock;

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Address Field`}
      subtitle="Configure one field of the customer address form — where it sits in the address, how it is captured and whether it is required."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={680}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={geo ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!geo}>
            {geo ? `Complete these to enable ${actionLabel}:` : 'Start by choosing the Geo Level — where this field sits in the address.'}
          </Typography>
          {geo && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${label}: ${errors[key]}`}
                  color="warning"
                  variant="outlined"
                  onClick={() => focusField(key)}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {editing && (
        <Alert severity={geo?.core ? 'warning' : 'info'} sx={{ mb: 2 }}>
          Geo Level, Field Key and Code are locked after creation.
          {deleteNote ? ` ${deleteNote}.` : ''}
          {lockNote ? ` ${lockNote}.` : ''}
        </Alert>
      )}

      <FormSection title="Where it applies" divider>
        <FormGrid>
          <Grid item xs={12} data-field="geoLevel">
            {editing ? (
              <TextField
                fullWidth
                size="small"
                label="Geo Level"
                value={geo?.label ?? str(meta.geoLevel)}
                helperText="Locked"
                InputProps={lockAdornment}
              />
            ) : (
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Geo Level"
                value={str(meta.geoLevel)}
                onChange={(e) => applyGeo(geoLevels.find((g) => g.value === e.target.value))}
                onBlur={() => touch('geoLevel')}
                error={!!errorFor('geoLevel')}
                helperText={errorFor('geoLevel') || 'Country → State → City → District / Locality are linked; each needs the level above it. Use Custom field for anything else.'}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => (v ? geoLevels.find((g) => g.value === v)?.label : <Typography component="span" color="text.secondary">— Select —</Typography>),
                }}
              >
                {geoLevels.map((g) => {
                  const takenBy = !g.repeatable ? takenGeo.get(g.value) : undefined;
                  return (
                    <MenuItem key={g.value} value={g.value} disabled={!!takenBy}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{g.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {g.parent ? `Needs ${geoLevels.find((p) => p.value === g.parent)?.label}` : g.repeatable ? 'Any number of custom fields' : 'Top level'}
                            {g.fieldKey ? ` · saved as ${g.fieldKey}` : ''}
                          </Typography>
                        </Box>
                        {g.core && <Chip size="small" label="Core" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                        {takenBy && <Chip size="small" label={`Added as "${takenBy}"`} sx={{ height: 18, fontSize: 10 }} />}
                      </Stack>
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                {HIERARCHY.map((level, i) => {
                  const def = geoLevels.find((g) => g.value === level);
                  const row = others.find((r) => geoOf(r) === level);
                  const current = geo?.value === level;
                  return (
                    <Stack key={level} direction="row" alignItems="center" gap={0.5}>
                      {i > 0 && <ArrowForward sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      <Chip
                        size="small"
                        label={`${def?.label ?? level}${row ? ` · #${orderOf(row)}` : current ? ' · this field' : ' · not added'}`}
                        color={current ? 'primary' : row ? (row.isActive === false ? 'default' : 'success') : 'default'}
                        variant={current ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {parentDef
                  ? parentRow
                    ? `${geo!.label} is filtered by the customer's ${parentDef.label} ("${parentRow.name}", sort order ${orderOf(parentRow)}) — it must come after it in the form.`
                    : `Add the ${parentDef.label} field first — ${geo!.label} depends on it.`
                  : geo?.anchor
                    ? 'Country anchors the address — State and City options are filtered by it.'
                    : 'The address form shows fields in sort order; dependent levels always follow their parent.'}
              </Typography>
            </Paper>
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Basic info" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Display name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => {
                touch('name');
                const clean = normalizeAddressFieldName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Label the customer sees · unique · letters only (e.g. Flat Number)'}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="fieldKey">
            <TextField
              fullWidth
              size="small"
              required
              label="Field Key"
              value={str(meta.fieldKey)}
              disabled={!geo}
              onChange={(e) => {
                setKeyEdited(true);
                setMeta({ fieldKey: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 40) });
                touch('fieldKey');
              }}
              onBlur={() => touch('fieldKey')}
              error={!!errorFor('fieldKey')}
              helperText={errorFor('fieldKey') || (geo?.fieldKey
                ? `Fixed — saved on the customer address as "${geo.fieldKey}"`
                : 'Machine-readable property · lowercase, digits, _ · 2–40 chars · unique (e.g. flat_number)')}
              InputProps={editing || geo?.fieldKey ? { ...lockAdornment, sx: { fontFamily: 'monospace' } } : { sx: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={str(meta.fieldKey)}
              helperText="Auto — always equals the Field Key"
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Position in the customer form · unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, max: ADDRESS_FIELD_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Capture rules">
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="fieldType">
            {geo && geo.fieldTypes.length === 1 ? (
              <TextField
                fullWidth
                size="small"
                required
                label="Field Type"
                value={typeDefs.find((t) => t.value === geo.fieldTypes[0])?.label ?? geo.fieldTypes[0]}
                helperText={`Fixed for ${geo.label} — ${typeHint ?? ''}`}
                InputProps={lockAdornment}
              />
            ) : (
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Field Type"
                value={str(meta.fieldType)}
                disabled={!geo}
                onChange={(e) => { setMeta({ fieldType: e.target.value }); touch('fieldType'); }}
                error={!!errorFor('fieldType')}
                helperText={errorFor('fieldType') || typeHint || 'Choose the Geo Level first — allowed types depend on it'}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                {!str(meta.fieldType) && <MenuItem value="" disabled>— Select —</MenuItem>}
                {typeDefs.filter((t) => !geo || geo.fieldTypes.includes(t.value)).map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box>
                      <Typography variant="body2">{t.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.hint}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={6} data-field="source">
            {geo && geo.sources.length === 1 ? (
              <TextField
                fullWidth
                size="small"
                required
                label="Data Source"
                value={sourceDefs.find((s) => s.value === geo.sources[0])?.label ?? geo.sources[0]}
                helperText={str(meta.fieldType) === 'select'
                  ? `Dropdown options come from the ${geo.label === 'Country' ? 'Countries' : geo.label === 'City' ? 'Cities' : 'States / Provinces'} master${parentDef ? `, filtered by the chosen ${parentDef.label}` : ''}`
                  : `Fixed — ${sourceHint ?? ''}`}
                InputProps={lockAdornment}
              />
            ) : (
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Data Source"
                value={str(meta.source)}
                disabled={!geo}
                onChange={(e) => { setMeta({ source: e.target.value }); touch('source'); }}
                error={!!errorFor('source')}
                helperText={errorFor('source') || sourceHint || 'Choose the Geo Level first — allowed sources depend on it'}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                {!str(meta.source) && <MenuItem value="" disabled>— Select —</MenuItem>}
                {sourceDefs.filter((s) => !geo || geo.sources.includes(s.value)).map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    <Box>
                      <Typography variant="body2">{s.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.hint}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} data-field="required">
            <FormControlLabel
              control={(
                <Switch
                  checked={meta.required === true}
                  disabled={!geo || geo.anchor}
                  onChange={(e) => { setMeta({ required: e.target.checked }); touch('required'); }}
                />
              )}
              label={meta.required === true ? 'Required' : 'Optional'}
            />
            <Typography variant="caption" color={errorFor('required') ? 'error' : 'text.secondary'} display="block">
              {errorFor('required')
                || (geo?.anchor
                  ? `${geo.label} is part of the Country → State → City hierarchy and is always required.`
                  : meta.required === true
                    ? 'Customers must fill this field before they can save an address.'
                    : 'Customers can leave this field empty.')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Address Form Fields list to activate or deactivate this field.'
                : 'New fields are created as Active — use the status toggle in the list to deactivate later.'}
            </Typography>
          </Grid>
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
